// @theme/* resolve through the import map in snippets/scripts.liquid, which versions each URL
// with asset_url, so a new deploy is never served from a cached copy.
/* eslint-disable import/no-unresolved */
import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { nextStickyState } from "@theme/pdp-sticky-bar-state";
/* eslint-enable import/no-unresolved */

/**
 * The PDP sticky add-to-bag bar. Stands in for Horizon's <sticky-add-to-cart>, whose script is
 * gitignored in this repo, with the same markup and a calmer show/hide: positions measured once
 * per frame rather than IntersectionObserver booleans, and a gap between the show and hide
 * lines (see pdp-sticky-bar-state.js).
 *
 * Its button still presses the main Add to bag button, so the cart request, the cart drawer
 * and every tracking hook behave exactly as they do for that button.
 *
 * @typedef {object} PdpStickyBarRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 *
 * @extends {Component<PdpStickyBarRefs>}
 */
class PdpStickyBar extends Component {
  requiredRefs = ["stickyBar", "addToCartButton"];

  /** @type {"shown" | "hidden"} */
  #state = "hidden";

  #abortController = new AbortController();

  #frame = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addedTimeout;

  /**
   * The sticky bar's button: press the main one.
   */
  handleAddToCartClick = () => {
    const main = this.#mainButton();
    if (!main) return;
    main.dataset.puppet = "true";
    main.click();

    const { addToCartButton } = this.refs;
    addToCartButton.dataset.added = "true";
    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => {
      addToCartButton.removeAttribute("data-added");
    }, 800);
  };

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update(false);
    });
  };

  #restore = () => {
    this.#update(true);
  };

  #resetPuppet = () => {
    const main = this.#mainButton();
    if (main) main.dataset.puppet = "false";
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
if (String(event.detail.data.productId) !== this.dataset.productId) return;

    const next = event.detail.data.html.querySelector("pdp-sticky-bar");
    const nextBar = next?.querySelector('[ref="stickyBar"]');
    if (!next || !nextBar) return;

    morph(this.refs.stickyBar, nextBar, { childrenOnly: true });
    this.refs.stickyBar.dataset.stuck = String(this.#state === "shown");
    this.dataset.variantAvailable = next.dataset.variantAvailable;

    // No variant for the chosen options: the stylesheet hides the bar, as Horizon's does.
    const variant = event.detail.resource;
    this.dataset.currentVariantId = variant?.id ? String(variant.id) : "";

    // The update re-renders the section and can move the main button without a scroll, so
    // re-measure rather than keep the state from the last scroll.
    this.#schedule();
  };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const passive = { passive: true, signal };
    window.addEventListener("scroll", this.#schedule, passive);
    window.addEventListener("resize", this.#schedule, passive);
    // The iOS toolbar collapsing resizes the visual viewport, not the window.
    window.visualViewport?.addEventListener("resize", this.#schedule, passive);
    // Back/forward cache: the page returns mid-scroll, so settle without animating.
    window.addEventListener("pageshow", this.#restore, { signal });

    const section = this.closest(".shopify-section");
    section?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    document.addEventListener(ThemeEvents.cartUpdate, this.#resetPuppet, {
      signal,
    });
    document.addEventListener(ThemeEvents.cartError, this.#resetPuppet, {
      signal,
    });

    this.#update(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    clearTimeout(this.#addedTimeout);
  }

  /**
   * Measure, decide, and write `data-stuck` only when the state changes.
   *
   * @param {boolean} instant settle without the slide, for page load and bfcache restores
   */
  #update(instant) {
    const main = this.#mainButton();
    if (!main) return;

    const button = main.getBoundingClientRect();
    const header = document
      .querySelector("#header-component")
      ?.closest(".header-section");
    const footer = document.querySelector("footer");

    const next = nextStickyState(this.#state, {
      buttonTop: button.top,
      buttonBottom: button.bottom,
      coverBottom: header ? header.getBoundingClientRect().bottom : 0,
      viewportHeight: window.visualViewport?.height ?? window.innerHeight,
      footerTop: footer
        ? footer.getBoundingClientRect().top
        : Number.POSITIVE_INFINITY,
    });
    if (next === this.#state && !instant) return;

    const { stickyBar } = this.refs;
    this.#state = next;
    if (!instant) {
      stickyBar.dataset.stuck = String(next === "shown");
      return;
    }
    // Apply the state with transitions off, then force a style flush so the browser commits it
    // before they come back on. Removing the attribute in a later frame is not enough: rAF runs
    // before style recalc, so the slide would still play.
    stickyBar.dataset.instant = "true";
    stickyBar.dataset.stuck = String(next === "shown");
    stickyBar.getBoundingClientRect();
    delete stickyBar.dataset.instant;
  }

  /**
   * The main form's Add to bag button, in this section, for this product.
   *
   * @returns {HTMLButtonElement | null}
   */
  #mainButton() {
    const form = this.closest(".shopify-section")?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`,
    );
    return form?.querySelector('[ref="addToCartButton"]') ?? null;
  }
}

if (!customElements.get("pdp-sticky-bar")) {
  customElements.define("pdp-sticky-bar", PdpStickyBar);
}
